import { editorConfig } from "../constants.js";
import {
  type ChangeNotifier,
  type DirEntry,
  type FileSystemPort,
  type FileWithStat,
  PathNotFoundError,
  type PathStat,
  toSafeSegments,
} from "../ports.js";

interface MemoryFile {
  content: string | Uint8Array;
  mtime: Date;
}

export interface MemoryFileSystemOptions {
  /**
   * The project to start from, as project-relative path → file content. Parent
   * directories are implied and don't need listing.
   */
  files?: Record<string, string>;

  /**
   * Top-level directories whose changes are reported to `onChange` listeners.
   * Defaults to the same set chokidar watches on Node, so the preview's
   * live-reload behaviour matches the CLI's rather than being broader.
   */
  watchedDirs?: readonly string[];

  /** Injectable clock, so tests can control mtimes. */
  now?: () => Date;

  /**
   * The mtime the seeded project starts with. Defaults to now.
   *
   * Worth setting to a constant wherever the same seed is loaded more than
   * once — a Worker builds one of these per isolate, and if each picked its own
   * startup time, a client that read a file from one isolate would be told its
   * save conflicts when it landed on another. The file it read and the file it
   * is saving over are byte-identical; only the clock disagreed.
   */
  seedMtime?: Date;
}

export interface MemoryFileSystem extends FileSystemPort, ChangeNotifier {
  /** Discards all edits and returns to the seeded project. */
  reset(): void;
}

const decoder = new TextDecoder();

/**
 * A `FileSystemPort` held entirely in memory — what the per-PR preview Worker
 * runs against, since a Worker has no filesystem.
 *
 * Directories are implied by the paths of the files inside them, plus any
 * created explicitly, which is enough for a project tree and avoids maintaining
 * a parallel directory structure that could disagree with the files.
 *
 * It doubles as the `ChangeNotifier`: writes report themselves, so the SPA's
 * live-reload path stays exercised in the preview instead of going quiet with
 * no chokidar to drive it.
 */
export function createMemoryFileSystem(
  options: MemoryFileSystemOptions = {},
): MemoryFileSystem {
  const {
    files: seed = {},
    watchedDirs = editorConfig.watch.watchedDirs,
    now = () => new Date(),
    // One timestamp for the whole seed rather than one per file, so `reset()`
    // restores the project exactly as it was first loaded.
    seedMtime = now(),
  } = options;

  const files = new Map<string, MemoryFile>();
  /** Directories that exist without (yet) containing a file. */
  const emptyDirs = new Map<string, Date>();
  const listeners = new Set<(path: string) => void>();

  /** Canonicalises a path and refuses traversal, exactly as Node's adapter does. */
  const key = (path: string): string => toSafeSegments(path).join("/");

  const parentOf = (path: string): string =>
    path.slice(0, Math.max(0, path.lastIndexOf("/")));

  const isWatched = (path: string): boolean =>
    watchedDirs.some((dir) => path === dir || path.startsWith(`${dir}/`));

  /**
   * Announces a change the way chokidar does on Node — including for the
   * editor's own saves, which also trip the real watcher.
   */
  const emit = (path: string): void => {
    if (!isWatched(path)) {
      return;
    }
    for (const listener of listeners) {
      listener(path);
    }
  };

  /** True when anything lives under `path`, which is what makes it a directory. */
  const hasChildren = (path: string): boolean => {
    const prefix = `${path}/`;
    for (const filePath of files.keys()) {
      if (filePath.startsWith(prefix)) {
        return true;
      }
    }
    for (const dirPath of emptyDirs.keys()) {
      if (dirPath.startsWith(prefix)) {
        return true;
      }
    }
    return false;
  };

  const isDirectory = (path: string): boolean =>
    // The root always exists, even in an empty project.
    path === "" || emptyDirs.has(path) || hasChildren(path);

  /**
   * Records `path` and its ancestors as directories.
   *
   * They're only remembered while empty: once a file sits inside, the file's
   * own path is what makes the directory exist, and keeping a second record
   * would let the two disagree.
   */
  const addDirs = (path: string, timestamp: Date): void => {
    for (let dir = path; dir !== ""; dir = parentOf(dir)) {
      if (!files.has(dir)) {
        emptyDirs.set(dir, timestamp);
      }
    }
  };

  const write = (
    path: string,
    content: string | Uint8Array,
    at?: Date,
  ): PathStat => {
    const mtime = at ?? now();
    // A file and a directory can't share a path, and the file wins — the same
    // way writing over a directory on disk fails rather than merging.
    emptyDirs.delete(path);
    addDirs(parentOf(path), mtime);
    files.set(path, { content, mtime });
    return { type: "file", mtime };
  };

  const load = (): void => {
    files.clear();
    emptyDirs.clear();
    for (const [path, content] of Object.entries(seed)) {
      write(key(path), content, seedMtime);
    }
  };

  load();

  return {
    onChange(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    reset() {
      load();
    },

    // Every method below is `async` even though nothing here awaits: it makes a
    // rejected promise the only way these fail, matching the Node adapter. A
    // sync throw from `key()` on a traversal attempt would otherwise escape
    // before the promise existed, and callers would have to guard both ways.
    async readFileWithStat(path: string): Promise<FileWithStat> {
      const file = files.get(key(path));
      if (!file) {
        throw new PathNotFoundError(path);
      }

      // Content and mtime come from one immutable record, so they always
      // describe the same version — the property the Node adapter needs an open
      // file handle to get.
      return {
        content:
          typeof file.content === "string"
            ? file.content
            : decoder.decode(file.content),
        mtime: file.mtime,
      };
    },

    async listDir(path: string): Promise<DirEntry[]> {
      const dir = key(path);
      if (!isDirectory(dir)) {
        throw new PathNotFoundError(path);
      }

      const prefix = dir === "" ? "" : `${dir}/`;
      const entries = new Map<string, DirEntry>();

      const collect = (fullPath: string, type: DirEntry["type"]): void => {
        if (!fullPath.startsWith(prefix)) {
          return;
        }
        const rest = fullPath.slice(prefix.length);
        if (rest === "") {
          return;
        }
        const slash = rest.indexOf("/");
        // A path continuing past the first segment describes a grandchild, so
        // the segment itself is a directory rather than the entry.
        const name = slash === -1 ? rest : rest.slice(0, slash);
        entries.set(name, {
          name,
          type: slash === -1 ? type : "directory",
        });
      };

      for (const filePath of files.keys()) {
        collect(filePath, "file");
      }
      for (const dirPath of emptyDirs.keys()) {
        collect(dirPath, "directory");
      }

      return [...entries.values()];
    },

    async statPath(path: string): Promise<PathStat | undefined> {
      const target = key(path);

      const file = files.get(target);
      if (file) {
        return { type: "file", mtime: file.mtime };
      }

      if (isDirectory(target)) {
        return {
          type: "directory",
          mtime: emptyDirs.get(target) ?? now(),
        };
      }

      return undefined;
    },

    async writeFile(
      path: string,
      content: string | Uint8Array,
    ): Promise<PathStat> {
      const target = key(path);
      const stat = write(target, content);
      emit(target);
      return stat;
    },

    async remove(path: string): Promise<void> {
      const target = key(path);
      if (!files.delete(target)) {
        throw new PathNotFoundError(path);
      }
      // The parent may have just become empty, and an empty directory still
      // exists on disk after its last file is deleted.
      addDirs(parentOf(target), now());
      emit(target);
    },

    async rename(from: string, to: string): Promise<void> {
      const fromKey = key(from);
      const toKey = key(to);

      const file = files.get(fromKey);
      if (!file) {
        throw new PathNotFoundError(from);
      }

      files.delete(fromKey);
      addDirs(parentOf(fromKey), now());
      // Carries its mtime across, as a rename on disk does — the content didn't
      // change, so a client holding a `Last-Modified` for it stays valid.
      emptyDirs.delete(toKey);
      addDirs(parentOf(toKey), file.mtime);
      files.set(toKey, file);

      emit(fromKey);
      emit(toKey);
    },

    async mkdirp(path: string): Promise<void> {
      const target = key(path);
      if (target !== "") {
        addDirs(target, now());
      }
    },
  };
}
